import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Search, Image as ImageIcon, Check, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * MediaGalleryPicker - A modal component that allows selecting images from the Media Gallery
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when an image is selected, receives the image_url
 * @param {string} currentUrl - Currently selected image URL (optional, for highlighting)
 */
export default function MediaGalleryPicker({ isOpen, onClose, onSelect, onSelectMedia, currentUrl = "" }) {
  const [pictures, setPictures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUrl, setSelectedUrl] = useState(currentUrl);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const uploadInput = useRef(null);

  const loadPictures = useCallback(async (requestedPage = 1, append = false, search = searchQuery) => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/media", { params: { page: requestedPage, limit: 24, search } });
      const images = Array.isArray(data?.items) ? data.items : [];
      setPictures((current) => append ? [...current, ...images] : images);
      setSelectedPicture((selected) => images.find((image) => image.image_url === selectedUrl) || selected);
      setTotal(Number(data?.total) || 0);
      setPage(Number(data?.page) || requestedPage);
      setPages(Number(data?.pages) || 1);
    } catch (error) {
      toast.error("Failed to load pictures from gallery");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (isOpen) {
      setSelectedUrl(currentUrl);
      loadPictures(1, false, searchQuery);
    }
  }, [isOpen, currentUrl, searchQuery, loadPictures]);

  const filteredPictures = pictures;

  const handleSelect = (picture) => {
    setSelectedUrl(picture.image_url);
    setSelectedPicture(picture);
  };

  const handleConfirm = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      if (onSelectMedia && selectedPicture) onSelectMedia(selectedPicture);
      onClose();
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file");
    const payload = new FormData();
    payload.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/api/media/upload", payload, {_noOfflineQueue:true});
      const imageUrl = data?.image_url;
      if (!imageUrl) throw new Error("Upload did not return an image URL");
      setPictures((current) => [data, ...current.filter((picture) => picture.image_url !== imageUrl)]);
      setTotal((count) => count + 1);
      setSelectedUrl(imageUrl);
      setSelectedPicture(data);
      toast.success("Image uploaded and selected");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div role="dialog" aria-modal="true" aria-label="Select from Media Gallery" className="relative w-full max-w-5xl bg-zinc-900 rounded-2xl border border-white/10 shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <ImageIcon size={20} className="text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Select from Media Gallery</h2>
              <p className="text-sm text-zinc-400">{total} images available</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close media gallery"
            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors"
          >
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        {/* Search */}
        <div className="flex flex-col gap-3 border-b border-white/10 p-6 sm:flex-row">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search by filename, tag, or alt text..."
              aria-label="Search media gallery"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-purple-500/50"
            />
          </div>
          <input ref={uploadInput} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
          <button type="button" onClick={() => uploadInput.current?.click()} disabled={uploading} className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white transition hover:bg-white/10 disabled:opacity-50"><Upload size={16} />{uploading ? "Uploading…" : "Upload Image"}</button>
        </div>

        {/* Gallery Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : total === 0 && !searchQuery.trim() ? (
            <div className="flex h-64 flex-col items-center justify-center text-zinc-500">
              <ImageIcon size={48} className="mb-3 opacity-30" />
              <p className="text-lg font-medium">No images in your gallery yet.</p>
              <p className="text-sm">Upload an image to use it in an ad placement.</p>
              <button type="button" onClick={() => uploadInput.current?.click()} disabled={uploading} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Upload size={15} />Upload Image</button>
            </div>
          ) : filteredPictures.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-zinc-500">
              <ImageIcon size={48} className="mb-3 opacity-30" />
              <p className="text-lg font-medium">No images found</p>
              <p className="text-sm">Try a different search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredPictures.map((pic) => {
                const isSelected = selectedUrl === pic.image_url;
                return (
                  <button
                    key={pic.id}
                    type="button"
                    onClick={() => handleSelect(pic)}
                    aria-pressed={isSelected}
                    className={`relative group overflow-hidden rounded-xl border-2 text-left transition-all ${isSelected ? "border-purple-500 shadow-lg shadow-purple-500/30" : "border-white/10 hover:border-purple-500/50"}`}
                  >
                    <div className="aspect-square bg-black/40">
                      <img
                        src={pic.thumbnail_url || pic.image_url}
                        alt={pic.alt_text || pic.title || pic.filename || "Gallery image"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {isSelected && (
                      <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                          <Check size={20} className="text-white" />
                        </div>
                      </div>
                    )}
                    <div className="space-y-1 bg-zinc-950 p-2">
                      <p className="truncate text-xs font-medium text-white">{pic.filename || pic.title || "Image"}</p>
                      <p className="text-[10px] text-zinc-500">{pic.upload_date ? new Date(pic.upload_date).toLocaleDateString() : "Date unavailable"}{pic.width && pic.height ? ` · ${pic.width} × ${pic.height}` : ""}</p>
                    </div>
                  </button>
                );
              })}
              {page < pages && <button type="button" disabled={loading} onClick={() => loadPictures(page + 1, true)} className="col-span-full min-h-10 rounded-lg border border-white/10 text-sm text-zinc-300 hover:bg-white/5 disabled:opacity-50">{loading ? "Loading…" : "Load more images"}</button>}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-white/10 flex items-center justify-between">
          <p className="text-sm text-zinc-400">
            {selectedUrl ? "Image selected" : "Click an image to select"}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedUrl}
              className={`
                px-6 py-2 rounded-xl font-medium transition-all
                ${selectedUrl
                  ? "bg-purple-500 hover:bg-purple-600 text-white shadow-lg shadow-purple-500/30"
                  : "bg-white/5 text-zinc-500 cursor-not-allowed"
                }
              `}
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
